import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ProgressRing from './ProgressRing';
import useStreak from '../hooks/useStreak';
import { LABEL_MICRO, LABEL_SM, TITLE_SECTION } from '../utils/typeScale';
import { RED } from '../utils/brandColors';

DailyQuestCard.propTypes = {
  compact: PropTypes.bool,
};

/**
 * The daily goal, read from the server.
 *
 * This used to keep its own count in a `daily_quest_<date>_<userId>`
 * localStorage key with a goal of 5, polled once a second, while the streak
 * endpoint independently tracked `daily_vote_count` against a goal of 3. The
 * two disagreed constantly and the localStorage copy did not survive a device
 * change. `daily_vote_goal` is now the only source.
 *
 * The milestone chip row is gone as well — it restated the ring.
 */
export default function DailyQuestCard({ compact = false }) {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const { streak } = useStreak();

  const goal = streak?.daily_vote_goal ?? 3;
  // Clamped: `daily_vote_count` is a raw running total that legitimately
  // exceeds the goal, and this ring means "progress toward today's goal".
  const progress = Math.min(streak?.daily_vote_count ?? 0, goal);
  const isCompleted = progress >= goal;
  const size = compact ? 40 : 64;
  const strokeWidth = compact ? 4 : 6;

  const remaining = Math.max(0, goal - progress);
  const body = isCompleted
    ? 'Your streak is safe until tomorrow.'
    : `Vote ${remaining} more time${remaining === 1 ? '' : 's'} to keep your streak.`;

  return (
    <Box
      sx={{
        p: compact ? 1.75 : { xs: 2, sm: 2.5 },
        mb: compact ? 0 : 3,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 1.75 : 2.5,
      }}
    >
      <ProgressRing
        value={progress}
        goal={goal}
        size={size}
        strokeWidth={strokeWidth}
        color={RED}
        trackColor={alpha(RED, 0.15)}
        reducedMotion={prefersReducedMotion}
      >
        <Typography
          sx={{
            fontSize: compact ? 12 : 15,
            fontWeight: 900,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {progress}/{goal}
        </Typography>
      </ProgressRing>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={LABEL_MICRO}>Daily goal</Typography>
        {compact ? (
          <Typography sx={{ ...LABEL_SM, mt: 0.25 }}>{body}</Typography>
        ) : (
          <>
            <Typography variant="h6" sx={{ ...TITLE_SECTION, mt: 0.25 }}>
              {isCompleted ? "Today's goal is done" : 'Keep the streak alive'}
            </Typography>
            <Typography sx={{ ...LABEL_SM, mt: 0.25 }}>{body}</Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
